import React, { useState, useRef, useEffect } from "react";
import { Box, Card, CardContent, Typography, Grid, IconButton, Chip } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime"; // ⏰ Kellaaeg
import PersonIcon from "@mui/icons-material/Person"; // 👤 Treener
import GroupIcon from "@mui/icons-material/Group"; // 👥 Mahutavus
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents"; // 🏆 Trophy icon for leaderboard
import CheckCircleIcon from "@mui/icons-material/CheckCircle"; // ✅ Registreerimise märk
import LeaderboardModal from "./LeaderboardModal";
import ScheduleIcon from "@mui/icons-material/Schedule"; // Import LeaderboardModal
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard"; // Free class icon
import { formatClassTimeOnly } from "../utils/classModalHelpers";

export default function ClassSchedule({ classes, attendeesCount, onClassClick, weeklyView, hideTrainer = false }) {
    // ✅ Sorteerime klassid ajaliselt (varasemad eespool)
    const sortedClasses = [...classes].sort((a, b) => {
        const timeA = new Date(a.time);
        const timeB = new Date(b.time);

        // Esmalt sorteerime aja järgi
        if (timeA.getTime() !== timeB.getTime()) {
            return timeA - timeB;
        }

        // Kui ajad on samad, sorteerime mahutavuse järgi (väiksemad eespool)
        return (a.memberCapacity || 0) - (b.memberCapacity || 0);
    });

    // State for Leaderboard modal
    const [isLeaderboardOpen, setLeaderboardOpen] = useState(false);
    const [selectedLeaderboardClass, setSelectedLeaderboardClass] = useState(null);

    // Ref for scrolling to next available class
    const classRefs = useRef({});
    const containerRef = useRef(null);
    const classesRef = useRef(classes);
    classesRef.current = classes;
    const scheduleKey = classes
        .map((cls) => `${cls.id}:${cls.time}`)
        .sort()
        .join("|");

    // Handle opening the leaderboard
    const handleOpenLeaderboard = (e, cls) => {
        e.stopPropagation(); // Prevent the card click from opening the class modal
        setSelectedLeaderboardClass(cls);
        setLeaderboardOpen(true);
    };

    // Handle closing the leaderboard
    const handleCloseLeaderboard = () => {
        setLeaderboardOpen(false);
        setSelectedLeaderboardClass(null);
    };

    // Function to get background color based on training type
    const getBackgroundColor = (trainingType) => {
        switch(trainingType) {
            case "WOD":
                return "#f0faff"; // hele-sinine
            case "Weightlifting":
                return "#f0fff5"; // hele-roheline
            case "Cardio":
                return "#fff5f5"; // hele-punane
            case "Rowing":
                return "#f9f0ff"; // hele-lilla
            case "Gymnastics":
                return "#fff0f9"; // hele-roosa
            case "Open Gym":
                return "#f5f0e1"; // hele-pruun
            case "Kids":
                return "#cae1ca"; // hele-kollane
            case "Basic":
                return "#f0f0ff"; // hele-hall
            case "Hyrox":
                return "#ffe9e9"; // hele-taevasinine
            case "Hybrid":
                return "#ffe9e9"; // hele-sinine
            case "Mobility":
                return "#e0f7fa"; // hele-turquoise
            default:
                return "background.paper";
        }
    };

    useEffect(() => {
        const currentSortedClasses = [...classesRef.current].sort((a, b) => {
            const timeDifference = new Date(a.time) - new Date(b.time);
            return timeDifference || (a.memberCapacity || 0) - (b.memberCapacity || 0);
        });

        if (currentSortedClasses.length === 0) return;

        const now = new Date();
        const nextAvailableClass = currentSortedClasses.find(cls => {
            const classTime = new Date(cls.time);
            return classTime > now;
        });

        if (nextAvailableClass && classRefs.current && classRefs.current[nextAvailableClass.id]) {
            // Small delay to ensure the component is fully rendered
            setTimeout(() => {
                // Add null check here
                if (classRefs.current[nextAvailableClass.id]) {
                    classRefs.current[nextAvailableClass.id].scrollIntoView({
                        behavior: 'smooth',
                        block: 'center', // Proovi 'center' asemel 'start'
                        inline: 'nearest'
                    });
                }
            }, 50);
        }
    }, [scheduleKey]);

    return (
        <Box ref={containerRef} sx={{ width: "100%", pb: 0, pt: 4 }}>
            {sortedClasses.length === 0 ? (
                <Typography
                    variant={weeklyView ? "body2" : "h6"}
                    align="center"
                    sx={{ width: "100%", mt: 2 }}
                >
                    No trainings scheduled.
                </Typography>
            ) : (
                sortedClasses.map((cls) => {
                    const classTime = new Date(cls.time);
                    const now = new Date();
                    const isPastClass = now > classTime;

                    return (
                        <Card
                            key={cls.id}
                            ref={el => classRefs.current[cls.id] = el}
                            sx={{
                                cursor: "pointer",
                                ":hover": { boxShadow: isPastClass ? 2 : 6 },
                                bgcolor: getBackgroundColor(cls.trainingType),
                                width: "100%",
                                mb: 2,
                                p: weeklyView ? 1 : 2,
                                position: "relative",
                                // Möödunud klasside stiil
                                opacity: isPastClass ? 0.6 : 1,
                                filter: isPastClass ? 'grayscale(70%)' : 'none',
                                transition: 'all 0.3s ease',
                                // Highlight next available class
                                boxShadow: (!isPastClass && sortedClasses.find(c => new Date(c.time) > now) === cls)
                                    ? '0 4px 20px rgba(33, 150, 243, 0.3)'
                                    : 'initial',
                                ...(cls.isRegistered && {
                                    border: '2px solid rgba(76, 175, 80, 0.3)',
                                    opacity: 1,
                                    transform: 'scale(1.01)',
                                }),
                                ...(cls.isInWaitlist && !cls.isRegistered && {
                                    border: '2px solid rgba(255, 152, 0, 0.3)',
                                }),
                            }}
                            onClick={() => onClassClick(cls)}
                        >
                            {/* Registration/Waitlist status icons */}
                            {(cls.isRegistered || cls.isInWaitlist) && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: 5,
                                        right: 5,
                                        zIndex: 1,
                                        color: cls.isRegistered ? "#4caf50" : "#ff9800", // roheline registreeritud, oranž waitlist
                                        width: 32,
                                        height: 32,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    {cls.isRegistered ? (
                                        <CheckCircleIcon />
                                    ) : (
                                        <ScheduleIcon /> // Waitlisti ikoon
                                    )}
                                </Box>
                            )}

                            {/* Free Class Indicator */}
                            {cls.freeClass && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: 5,
                                        left: 5,
                                        zIndex: 1,
                                    }}
                                >
                                    <Chip
                                        icon={<CardGiftcardIcon />}
                                        label="FREE"
                                        size="small"
                                        color="success"
                                        sx={{
                                            fontWeight: "bold",
                                            backgroundColor: "#4caf50",
                                            color: "white",
                                        }}
                                    />
                                </Box>
                            )}

                            <CardContent>
                                {weeklyView ? (
                                    // ✅ Päevavaade (KAHE VEERUGA)
                                    <Grid container spacing={2} alignItems="center">
                                        {/* Vasak veerg: Kellaaeg ja kestus */}
                                        <Grid item xs={3}>
                                            <Box display="flex" alignItems="center">
                                                <AccessTimeIcon sx={{ mr: 1 }} />
                                                <Typography>
                                                    {formatClassTimeOnly(cls.time)}
                                                </Typography>
                                            </Box>
                                            <Typography color="textSecondary">{cls.duration} min</Typography>
                                        </Grid>

                                        {/* Parem veerg: Klass, Treener, Mahutavus */}
                                        <Grid item xs={9}>
                                            <Typography variant="h6">{cls.trainingName}</Typography>

                                            {!hideTrainer && cls.trainer && (
                                                <Box display="flex" alignItems="center">
                                                    <PersonIcon sx={{ mr: 1 }} />
                                                    <Typography>{cls.trainer}</Typography>
                                                </Box>
                                            )}

                                            {cls.memberCapacity > 0 && (
                                                <Box display="flex" alignItems="center">
                                                    <GroupIcon sx={{ mr: 1 }} />
                                                    <Typography>
                                                        {cls.enrolledCount || 0} / {cls.memberCapacity}
                                                        {cls.waitlistCount > 0 && (
                                                            <span style={{ color: '#ff9800' }}>
        {` (${cls.waitlistCount})`}
    </span>
                                                        )}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Grid>
                                    </Grid>
                                ) : (
                                    // ✅ Nädalavaade (ÜHE VEERUGA)
                                    <Box display="flex" flexDirection="column" alignItems="center">
                                        <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.65rem" }}>
                                            {cls.trainingName}
                                        </Typography>

                                        <Box display="flex" alignItems="center" sx={{ fontSize: "0.65rem", mt: 0.5 }}>
                                            <AccessTimeIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                            <Typography variant="body2" sx={{ fontSize: "0.65rem"}}>
                                                {formatClassTimeOnly(cls.time)}
                                            </Typography>
                                        </Box>

                                        {!hideTrainer && cls.trainer && (
                                            <Box display="flex" alignItems="center" sx={{ fontSize: "0.65rem", mt: 0.5 }}>
                                                <PersonIcon sx={{ fontSize: 12, mr: 0.5 }} />
                                                <Typography variant="body2" sx={{ fontSize: "0.65rem"}}>{cls.trainer}</Typography>
                                            </Box>
                                        )}

                                        <Box display="flex" alignItems="center" sx={{ fontSize: "0.65rem", mt: 0.5 }}>
                                            <GroupIcon sx={{ fontSize: 12, mr: 0.5 }} />
                                            <Typography variant="body2" sx={{ fontSize: "0.65rem"}}>
                                                {cls.enrolledCount || 0} / {cls.memberCapacity}
                                                {cls.waitlistCount > 0 && (
                                                    <span style={{ color: '#ff9800' }}>
        {` (${cls.waitlistCount})`}
    </span>
                                                )}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    );
                })
            )}

            {/* Leaderboard Modal */}
            {selectedLeaderboardClass && (
                <LeaderboardModal
                    open={isLeaderboardOpen}
                    onClose={handleCloseLeaderboard}
                    classId={selectedLeaderboardClass.id}
                    cls={selectedLeaderboardClass}
                />
            )}
        </Box>
    );
}